import React from 'react';
import { Link } from 'react-router-dom';
import {
  Home as HomeIcon,
  MessageCircle,
  Footprints,
  ShoppingCart,
  ClipboardList,
  GraduationCap,
  ShieldCheck,
  HeartPulse,
  BadgeCheck,
  Wallet,
  HandHeart,
  Users,
  Clock,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Phone,
  Mail,
  HeartHandshake,
} from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';

const HERO_IMG =
  'https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&q=80&w=900';
const FAMILY_IMG =
  'https://images.unsplash.com/photo-1610986719243-7cdf28a29772?crop=entropy&cs=srgb&fm=jpg&q=80&w=800';

const services = [
  {
    icon: HomeIcon,
    title: 'Haushaltsnahe Dienstleistungen',
    items: [
      'Allgemeine Reinigungsarbeiten im Haushalt',
      'Reinigung von Wohnräumen',
      'Fensterreinigung',
      'Unterstützung bei leichten alltäglichen Arbeiten',
      'Weitere haushaltsnahe Tätigkeiten im Rahmen unserer anerkannten Leistungen',
    ],
  },
  {
    icon: MessageCircle,
    title: 'Gesellschaft & Gespräche',
    items: [
      'Persönliche Gespräche',
      'Gesellschaft leisten',
      'Gemeinsam Zeit verbringen',
      'Unterstützung gegen Einsamkeit und soziale Isolation',
      'Förderung der sozialen Teilhabe',
    ],
  },
  {
    icon: Footprints,
    title: 'Spaziergänge & Begleitung',
    items: [
      'Gemeinsame Spaziergänge',
      'Begleitung bei alltäglichen Aktivitäten',
      'Begleitung zu Terminen oder Erledigungen',
      'Unterstützung dabei, aktiv am Alltag teilzunehmen',
    ],
  },
  {
    icon: ShoppingCart,
    title: 'Einkäufe & Besorgungen',
    items: [
      'Einkäufe erledigen',
      'Unterstützung bei alltäglichen Besorgungen',
      'Begleitung bei notwendigen Erledigungen',
      'Unterstützung bei der Organisation des täglichen Lebens',
    ],
  },
  {
    icon: ClipboardList,
    title: 'Organisatorische Unterstützung',
    items: [
      'Unterstützung bei organisatorischen Aufgaben',
      'Vorbereitung und Strukturierung alltäglicher Angelegenheiten',
      'Unterstützung bei der Vorbereitung eines Umzugs',
      'Hilfe bei Planung und Organisation eines Wohnungswechsels',
      'Allgemeine Unterstützung bei der Strukturierung des Alltags',
    ],
  },
];

const qualifications = [
  {
    icon: GraduationCap,
    title: '40-stündiger Grundkurs',
    text: 'Erforderlicher Grundkurs zur Unterstützung im Alltag nach den Vorgaben des Landes Niedersachsen.',
  },
  {
    icon: ShieldCheck,
    title: 'Erweitertes Führungszeugnis',
    text: 'Ein einwandfreies erweitertes Führungszeugnis – für Ihre Sicherheit und Ihr Vertrauen.',
  },
  {
    icon: HeartPulse,
    title: 'Erste-Hilfe-Kurs',
    text: 'Ein absolvierter Erste-Hilfe-Kurs gehört bei uns zur Grundausstattung.',
  },
  {
    icon: BadgeCheck,
    title: 'Persönliche Eignung',
    text: 'Herzliche, verantwortungsvolle Menschen mit der passenden persönlichen Eignung.',
  },
];

const benefits = [
  {
    icon: Sparkles,
    title: 'Mehr Selbstständigkeit',
    text: 'Unterstützung dabei, den Alltag möglichst selbstbestimmt zu gestalten.',
  },
  {
    icon: Users,
    title: 'Mehr soziale Teilhabe',
    text: 'Gespräche, Spaziergänge und gemeinsame Aktivitäten gegen Einsamkeit und Isolation.',
  },
  {
    icon: HandHeart,
    title: 'Mehr Entlastung',
    text: 'Unterstützung bei Haushalt, Einkäufen und alltäglichen Aufgaben.',
  },
  {
    icon: Clock,
    title: 'Mehr Zeit für Angehörige',
    text: 'Wir übernehmen einen Teil der alltäglichen Unterstützung und schaffen dadurch Entlastung.',
  },
  {
    icon: HomeIcon,
    title: 'Unterstützung zu Hause',
    text: 'Die Hilfe findet dort statt, wo sich die Menschen wohlfühlen – in ihrer vertrauten Umgebung.',
  },
];

const relativeSupport = [
  'Unterstützung bei Haushalt und Einkäufen',
  'Begleitung und Spaziergänge',
  'Gesellschaft und Gespräche',
  'Unterstützung bei alltäglichen Erledigungen',
  'Organisatorische Unterstützung',
  'Hilfe bei der Vorbereitung eines Umzugs oder Wohnungswechsels',
];

const notIncluded = [
  'Körperpflege',
  'Waschen und Duschen',
  'Intimpflege',
  'An- und Auskleiden im Rahmen der Körperpflege',
  'Medikamentengabe',
  'Medizinische Tätigkeiten',
  'Behandlungspflegerische Maßnahmen',
];

const faqs = [
  {
    q: 'Für wen ist die Alltagsbegleitung geeignet?',
    a: 'Für Menschen mit Pflegegrad, die im Alltag Unterstützung benötigen und gleichzeitig ihre Selbstständigkeit und Lebensqualität erhalten möchten. Darüber hinaus können auch private Personen ohne Pflegegrad, die einfach etwas Hilfe im Alltag benötigen, unsere Leistungen nutzen – in diesem Fall erfolgt die Abrechnung privat.',
  },
  {
    q: 'Welche Pflegegrade können die Leistungen nutzen?',
    a: 'Unsere Leistungen können grundsätzlich für Personen mit Pflegegrad 1 bis 5 im Rahmen der jeweiligen gesetzlichen Möglichkeiten genutzt werden.',
  },
  {
    q: 'Können Sie direkt mit der Pflegekasse abrechnen?',
    a: 'Ja. Sofern die entsprechenden Voraussetzungen erfüllt sind und entsprechendes Budget zur Verfügung steht, können wir die Abrechnung direkt mit der zuständigen Pflegekasse übernehmen.',
  },
  {
    q: 'Was passiert, wenn mein Entlastungsbetrag bereits ausgeschöpft ist?',
    a: 'Wenn mehr Unterstützung gewünscht wird, können zusätzliche Leistungen unter Umständen privat abgerechnet werden. Außerdem kann unter den gesetzlichen Voraussetzungen die Möglichkeit bestehen, bis zu 40 % der ambulanten Pflegesachleistungen für Angebote zur Unterstützung im Alltag einzusetzen.',
  },
  {
    q: 'Übernehmen Ihre Alltagshelfer auch die Körperpflege?',
    a: 'Nein. Körperpflege sowie medizinische und behandlungspflegerische Tätigkeiten gehören nicht zu unserem Leistungsangebot. Dafür ist der entsprechende ambulante Pflegedienst bzw. medizinisches Fachpersonal zuständig.',
  },
  {
    q: 'Sind Ihre Alltagshelfer qualifiziert?',
    a: 'Ja. Unsere Alltagshelfer verfügen über den erforderlichen 40-stündigen Grundkurs nach den Vorgaben des Landes Niedersachsen, ein einwandfreies erweitertes Führungszeugnis sowie einen absolvierten Erste-Hilfe-Kurs und sind für ihre Tätigkeit entsprechend geeignet.',
  },
];

const SectionBadge = ({ children }) => (
  <span className="inline-block px-4 py-2 bg-[#E6F0FF] text-[#0052CC] text-sm font-medium rounded-full mb-4">
    {children}
  </span>
);

const AlltagsbegleitungPage = () => {
  return (
    <div data-testid="alltagsbegleitung-page">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-gradient-to-b from-[#F5F9FF] to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#E6F0FF] text-[#0052CC] text-sm font-medium rounded-full mb-6">
                <ShieldCheck className="w-4 h-4" />
                Anerkannt nach § 45a/45b SGB XI in Niedersachsen
              </span>
              <h1
                className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Alltagsbegleitung &{' '}
                <span className="text-[#0052CC]">Unterstützung im Alltag</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Wir unterstützen Menschen mit Pflegegrad in ihrem Alltag – damit sie
                selbstständig, sicher und wohl in ihrer vertrauten Umgebung leben können.
                Herzlich, verlässlich und mit viel Zeit für den Menschen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/kontakt"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="hero-contact-button"
                >
                  Jetzt Kontakt aufnehmen
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="tel:044193925989"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                  data-testid="hero-phone-button"
                >
                  <Phone className="w-5 h-5" />
                  0441 93925989
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={HERO_IMG}
                  alt="Alltagsbegleiterin unterstützt herzlich eine ältere Dame zu Hause"
                  className="w-full h-[380px] lg:h-[480px] object-cover"
                  width="900"
                  height="600"
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
              <div className="glass-card absolute -bottom-6 -left-6 shadow-xl max-w-[240px]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#00C2B8] rounded-full flex items-center justify-center flex-shrink-0">
                    <HeartHandshake className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Für Pflegegrad 1–5</p>
                    <p className="text-sm text-slate-500">Abrechnung über die Pflegekasse möglich</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro / Ziel */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionBadge>Unser Angebot</SectionBadge>
          <h2
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Damit der Alltag wieder leichter wird
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Unser Ziel ist es, Menschen mit Pflegegrad im Alltag zu unterstützen, ihre
            Selbstständigkeit zu erhalten und ihnen möglichst lange ein angenehmes und
            selbstbestimmtes Leben in ihrer gewohnten Umgebung zu ermöglichen. Dabei übernehmen
            wir alltägliche Aufgaben, entlasten Angehörige und bieten gleichzeitig persönliche
            und soziale Unterstützung.
          </p>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="alltag-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <SectionBadge>Unsere Leistungen</SectionBadge>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Unterstützung, die zum Leben passt
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Von haushaltsnahen Tätigkeiten bis zur gemeinsamen Zeit – wir sind für die kleinen
              und großen Dinge des Alltags da.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={index} className="service-card group flex flex-col" data-testid={`alltag-service-${index}`}>
                <div className="w-14 h-14 bg-[#E6F0FF] rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#0052CC] transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-[#0052CC] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3
                  className="text-xl font-semibold text-slate-900 mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {service.title}
                </h3>
                <ul className="space-y-3">
                  {service.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#00C2B8] mt-1 flex-shrink-0" />
                      <span className="text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Qualifizierte Alltagshelfer */}
      <section className="py-20 md:py-28 bg-[#0052CC] text-white" data-testid="alltag-qualification">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="inline-block px-4 py-2 bg-white/15 text-white text-sm font-medium rounded-full mb-4">
              Qualifizierte Alltagshelfer
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Bei uns sind Sie in guten Händen.
            </h2>
            <p className="text-lg text-blue-100 leading-relaxed">
              Unsere Alltagshelfer sind für ihre Tätigkeit entsprechend qualifiziert und vor
              Beginn umfassend vorbereitet. Wir legen großen Wert auf Qualität, Zuverlässigkeit,
              Vertrauen und einen verantwortungsvollen Umgang mit den uns anvertrauten Menschen.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualifications.map((q, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-6"
                data-testid={`qualification-${index}`}
              >
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4">
                  <q.icon className="w-6 h-6 text-[#0052CC]" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {q.title}
                </h3>
                <p className="text-sm text-blue-100 leading-relaxed">{q.text}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-blue-100 text-sm max-w-2xl mx-auto mt-10">
            Hinweis: Unsere Mitarbeiter sind qualifizierte Alltagshelfer – keine Pflegekräfte. Sie
            unterstützen bei den alltäglichen und sozialen Aufgaben, die zu unserem anerkannten
            Leistungsangebot gehören.
          </p>
        </div>
      </section>

      {/* Abrechnung über die Pflegekasse */}
      <section className="py-20 md:py-28 bg-white" data-testid="alltag-billing">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <SectionBadge>Abrechnung über die Pflegekasse</SectionBadge>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Sie haben einen Pflegegrad? Wir kümmern uns um die Abrechnung.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Unsere Alltagsbegleitung kann für Menschen mit Pflegegrad 1 bis 5 über die
              Pflegekasse abgerechnet werden. Entscheidend ist nicht der Pflegegrad, sondern ob
              der zur Verfügung stehende Entlastungsbetrag noch nicht ausgeschöpft ist. Steht
              noch Budget zur Verfügung, können unsere anerkannten Leistungen daraus in Anspruch
              genommen werden – und wir rechnen direkt mit der zuständigen Pflegekasse ab.
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#0052CC] to-[#0041A3] rounded-3xl p-8 md:p-12 text-white text-center shadow-xl mb-12">
            <Wallet className="w-10 h-10 mx-auto mb-4 text-white/90" />
            <p
              className="text-2xl md:text-3xl font-bold"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Weniger Papierkram. Weniger Aufwand. Mehr Entlastung.
            </p>
            <p className="text-blue-100 mt-4 max-w-xl mx-auto">
              Sie müssen sich nicht unnötig mit der Abrechnung beschäftigen – das übernehmen wir
              für Sie.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-8">
            <h3
              className="text-xl font-semibold text-slate-900 mb-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Sie möchten mehr Unterstützung? Wir zeigen Ihnen, welche Möglichkeiten Sie haben.
            </h3>
            <p className="text-slate-600 leading-relaxed">
              Möchten Sie mehr Leistungen in Anspruch nehmen, als über den verfügbaren
              Entlastungsbetrag abgerechnet werden können, gibt es weitere Wege: Der darüber
              hinausgehende Betrag kann privat abgerechnet werden. Zusätzlich besteht unter den
              gesetzlichen Voraussetzungen die Möglichkeit, bis zu 40 % der ambulanten
              Pflegesachleistungen für Angebote zur Unterstützung im Alltag einzusetzen. Gerne
              helfen wir Ihnen bei der Orientierung und der organisatorischen Abwicklung.
            </p>
            <p className="text-xs text-slate-400 mt-4">
              Die konkrete Nutzung der verschiedenen Budgets erfolgt entsprechend den jeweiligen
              gesetzlichen Voraussetzungen.
            </p>
          </div>
        </div>
      </section>

      {/* Entlastung für Angehörige */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="alltag-relatives">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img
                  src={FAMILY_IMG}
                  alt="Angehörige und Alltagsbegleiterin reichen sich unterstützend die Hände"
                  className="w-full h-[380px] object-cover"
                  width="800"
                  height="500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <SectionBadge>Entlastung für Angehörige</SectionBadge>
              <h2
                className="text-3xl md:text-4xl font-bold text-slate-900 mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Sie kümmern sich um Ihre Angehörigen – wir unterstützen Sie dabei.
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Alltagsbegleitung unterstützt nicht nur den Menschen mit Pflegegrad, sondern
                entlastet auch Angehörige. So gewinnen Sie wieder Zeit und Kraft – und es ist
                völlig in Ordnung, sich Unterstützung zu holen.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relativeSupport.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-[#00C2B8] mt-0.5 flex-shrink-0" />
                    <span className="text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warum Alltagsbegleitung */}
      <section className="py-20 md:py-28 bg-white" data-testid="alltag-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <SectionBadge>Warum Alltagsbegleitung?</SectionBadge>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Gut umsorgt – im eigenen Zuhause
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {benefits.map((b, index) => (
              <div
                key={index}
                className="text-center p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:border-[#BFDBFE] hover:shadow-md transition-all duration-300"
                data-testid={`benefit-${index}`}
              >
                <div className="w-14 h-14 bg-[#E6F0FF] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <b.icon className="w-7 h-7 text-[#0052CC]" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {b.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Abgrenzung zur ambulanten Pflege */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="alltag-scope">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <SectionBadge>Klare Aufgaben, klare Zuständigkeiten</SectionBadge>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Wir begleiten Sie im Alltag – Pflege übernimmt der Pflegedienst.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Unsere Alltagsbegleitung unterstützt bei alltäglichen Aufgaben und bei der sozialen
              Teilhabe. Für pflegerische und medizinische Aufgaben ist der dafür zuständige
              ambulante Pflegedienst bzw. das entsprechende medizinische Fachpersonal da.
            </p>
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-sm">
            <p className="text-slate-700 font-medium mb-5">
              Nicht Teil unseres Angebots sind insbesondere:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {notIncluded.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-500 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-white" data-testid="alltag-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <SectionBadge>Häufige Fragen</SectionBadge>
            <h2
              className="text-3xl md:text-4xl font-bold text-slate-900"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Alles, was Sie wissen möchten
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-slate-100 rounded-xl mb-3 px-5 bg-slate-50"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-base font-semibold text-slate-900 hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA / Kontakt */}
      <section className="py-20 md:py-28 bg-[#0052CC]" data-testid="alltag-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Sie wünschen sich Unterstützung im Alltag?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Wir beraten Sie gerne persönlich und besprechen gemeinsam, welche Unterstützung für
            Sie oder Ihre Angehörigen sinnvoll ist.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0052CC] px-8 py-4 rounded-full font-medium hover:bg-blue-50 transition-colors duration-300 shadow-lg"
              data-testid="alltag-cta-contact"
            >
              Jetzt Kontakt aufnehmen
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:044193925989"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-white border-2 border-white/30 px-8 py-4 rounded-full font-medium hover:bg-white/10 transition-colors duration-300"
              data-testid="alltag-cta-phone"
            >
              <Phone className="w-5 h-5" />
              0441 93925989
            </a>
          </div>
          <div className="mt-8 flex items-center justify-center gap-2 text-blue-100 text-sm">
            <Mail className="w-4 h-4" />
            <a href="mailto:service@bunterkopf-reinigung.de" className="hover:text-white transition-colors">
              service@bunterkopf-reinigung.de
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AlltagsbegleitungPage;
